<template>
  <main-large-dialog
    v-bind="$props"
    :is-visible.sync="computedIsVisible"
    :title="$i18n.t('dialog.title.patternDetails')"
    no-paddings-y
    no-paddings-x
    dialog-width="76%"
    max-height="90vh"
    bodyHeight="70vh"
    header-shadow
    agree-text="dialog.agreeSave"
    @modalClosed="onClose"
    @agree="handlePattern"
    :loading="isLoading"
    :is-valid="isValid"
  >
    <template #body>
      <v-col cols="4" class="fill-height" style="display: flex; flex-direction: column">
        <v-row no-gutters class="mt-2" align-content="center">
          <v-col cols="12" class="px-10 py-2">
            <v-form v-model="isFormValid">
              <main-input-dialog-row v-for="input in inputs" v-bind="input" :key="input.name">
                <template #[`name.input`]>
                  <v-col cols="6">
                    <data-input v-model="payload.name" v-on="$listeners" v-bind="input"></data-input>
                  </v-col>
                </template>
                <template #[`description.input`]>
                  <v-col cols="6">
                    <data-input v-model="payload.description" v-on="$listeners" v-bind="input"></data-input>
                  </v-col>
                </template>
              </main-input-dialog-row>
            </v-form>
          </v-col>
        </v-row>
        <v-row no-gutters class="borderTop py-3" align-content="center">
          <v-col>
            <label for="upload" class="py-2">
              <span aria-hidden="true" class="importButton"> {{ $i18n.t('button.importPattern') }}</span>
              <input type="file" id="upload" style="display:none" @change="importCSV" />
            </label>
          </v-col>
          <v-col>
            <base-button
              :ripple="false"
              :loading="isExportLoading"
              @click="exportData"
              :disabled="isDataEmpty"
              translation-path="button.exportPattern"
            ></base-button>
          </v-col>
        </v-row>
        <v-row no-gutters style="overflow-y: auto; height: 100%">
          <v-col>
            <v-row
              no-gutters
              class="py-3"
              :class="isDataEmpty ? 'tableHeaderNoChart' : 'tableHeader'"
              style="position: sticky; top: 0; background-color: white"
            >
              <v-col cols="6">{{ leftColumnTitle }}</v-col>
              <v-col cols="6">{{ rightColumnTitle }}</v-col>
            </v-row>
            <v-row no-gutters class="tableRow py-2" v-for="(line, index) in chartData" :key="index">
              <v-col cols="6">{{ index + 1 }}</v-col>
              <v-col cols="6">{{ line }}</v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="8" class="chart fill-height">
        <canvas id="patternChart" v-show="isChartVisible" class="pl-6 pt-10"></canvas>
      </v-col>
    </template>
  </main-large-dialog>
</template>

<script>
import { cloneDeep } from 'lodash';
import { call, get } from 'vuex-pathify';
import { parse as parseCsv } from 'csv-string';
import Chart from 'chart.js';

import MainInputDialogRow from '@/components/MainInputDialogRow';
import DataInput from '@/components/DataInput';
import BaseButton from '@/components/BaseButton';
import MainLargeDialog from '@/components/MainLargeDialog';

export default {
  name: 'ModelingPatternDialog',
  components: {
    BaseButton,
    MainLargeDialog,
    MainInputDialogRow,
    DataInput,
  },
  props: {
    isVisible: {
      type: Boolean,
      default: false,
    },
    pattern: {
      type: Object,
      required: true,
    },
  },
  data: vm => ({
    newPoint: {},
    payload: {
      name: vm.pattern.name || '',
      description: vm.pattern.description || '',
      multipliers: vm.pattern.multipliers || [],
    },
    isChartVisible: true,
    isLoading: false,
    chartData: [],
    isFormValid: false,
    chart: undefined,
    isExportLoading: false,
    isImportLoading: false,
    leftColumnTitle: vm.$i18n.t('dialog.body.patterns.time'),
    rightColumnTitle: vm.$i18n.t('dialog.body.patterns.factor'),
    fileUploaded: false,
  }),
  computed: {
    computedIsVisible: {
      get() {
        return this.isVisible;
      },
      set(nV) {
        this.$emit('update:isVisible', nV);
      },
    },
    patterns: get('epanet/patterns'),
    inputs() {
      return [
        {
          name: 'name',
          translationPath: 'dialog.body.curves.name',
          clearable: true,
          rules: [v => !!v || 'rules.required', v => !this.$_hasStringWhitespaces(v) || 'rules.notAllowedWhitespace'],
          dataType: {
            name: 'text',
          },
        },
        {
          name: 'description',
          translationPath: 'dialog.body.curves.description',
          clearable: true,
          rules: [v => !!v || 'rules.required'],
          dataType: {
            name: 'text',
          },
        },
      ];
    },
    isValid() {
      return this.isFormValid && !this.isDataEmpty;
    },
    isNew() {
      return !this.pattern.id;
    },
    handlePattern() {
      return this.isNew ? this.handleAdd : this.handleEdit;
    },
    isChart() {
      return !!this.chart;
    },
    nameAlreadyUsed() {
      return this.patterns
        .filter(pattern => pattern.name !== this.pattern.name)
        .some(pattern => pattern.name === this.payload.name);
    },
    isDataEmpty() {
      return this.chartData.length === 0;
    },
  },
  methods: {
    addPattern: call('epanet/addPattern'),
    editPattern: call('epanet/editPattern'),
    importCSV(data) {
      const file = data.target.files[0];
      const reader = new FileReader();
      reader.onload = evt => {
        this.parseCsvString(evt.target.result);
      };
      reader.readAsText(file);
    },
    parseCsvString(result) {
      const arr = parseCsv(result)[0];
      this.chartData = arr.map(v => parseFloat(v));
      this.computeChartData();
    },
    exportData() {
      this.saveToCsv();
    },
    onClose() {
      this.$emit('modalClosed');
    },
    resetPayload() {
      this.payload = undefined;
    },
    getCsvFileContent() {
      return this.chartData.toString();
    },
    getCsvBlobUrl() {
      const fileContent = this.getCsvFileContent();
      const file = new Blob([fileContent], { type: 'text/csv;charset=utf-8;' });
      return URL.createObjectURL(file);
    },
    saveToCsv() {
      this.$_saveFile(this.getCsvBlobUrl(), `${this.pattern.name}.csv`);
    },
    computeChartData() {
      const data = this.chartData;
      const labels = [];
      //TODO Dodac krok czasowy jako label.
      for (let i = 0; i < data.length; i++) {
        labels.push(i + 1);
      }
      this.isChartVisible = true;
      this.drawChart(labels, data);
    },
    drawChart(labels, data) {
      const canvas = document.getElementById('patternChart');
      if (this.isChart) {
        this.chart.destroy();
      }
      let stepSize = null;
      stepSize = Math.max(...data) < 1 ? 0.1 : (stepSize = Math.max(...data) < 10 ? 1 : 5);
      this.chart = new Chart(canvas, {
        options: {
          responsive: true,
          maintainAspectRatio: true,
          legend: {
            display: false,
          },
          elements: {
            point: {
              radius: 0,
            },
          },
          scales: {
            yAxes: [
              {
                ticks: {
                  callback: value => parseFloat(value).toFixed(1),
                  autoSkip: true,
                  stepSize: stepSize,
                  min: 0,
                },
                scaleLabel: {
                  display: true,
                  labelString: this.rightColumnTitle,
                },
              },
            ],
            xAxes: [
              {
                ticks: {
                  autoSkip: true,
                  stepSize: Math.max(...labels) < 1 ? 0.1 : 5,
                  max: Math.max(...labels) < 1 ? Math.max(...labels) : Math.ceil(Math.max(...labels) / 5) * 5,
                  min: 0,
                },
                scaleLabel: {
                  display: true,
                  labelString: this.leftColumnTitle,
                },
              },
            ],
          },
        },
        type: 'bar',
        data: {
          labels: labels,
          datasets: [
            {
              data: data,
              fill: false,
              lineTension: 1,
              borderColor: '#2a579a',
              backgroundColor: '#2a579a',
            },
          ],
        },
      });
    },
    async handleAdd() {
      this.isLoading = true;
      const editedPayload = this.payload;
      editedPayload.multipliers = this.chartData;
      if (this.nameAlreadyUsed) {
        this.$store.set('snackbar/PUSH_ERROR!', {
          message: this.$i18n.t('errors.patternAlreadyExists'),
        });
        this.isLoading = false;
        return;
      }
      try {
        await this.addPattern(editedPayload);
      } finally {
        this.isLoading = false;
        this.$emit('modalClosed');
        this.computedIsVisible = false;
        this.resetPayload();
      }
    },
    async handleEdit() {
      this.isLoading = true;
      const editedPayload = this.payload;
      editedPayload.multipliers = this.chartData;
      const data = { pattern_id: this.pattern.id, body: editedPayload };
      if (this.nameAlreadyUsed) {
        this.$store.set('snackbar/PUSH_ERROR!', {
          message: this.$i18n.t('errors.patternAlreadyExists'),
        });
        this.isLoading = false;
        return;
      }
      try {
        await this.editPattern(data);
      } finally {
        this.isLoading = false;
        this.$emit('modalClosed');
        this.computedIsVisible = false;
        this.resetPayload();
      }
    },
    async init() {
      await this.$nextTick();
      this.chartData = cloneDeep(this.payload.multipliers);
      if (!this.isDataEmpty) {
        this.computeChartData();
      }
    },
  },
  mounted() {
    this.init();
  },
};
</script>

<style scoped lang="scss">
.tableRow {
  @include border(bottom);
}
.tableHeader {
  @include border(top);
  @include border(bottom);
  @include border(left);
}
.tableHeaderNoChart {
  @include border(top);
  @include border(bottom);
  @include border(right);
}
.chart {
  @include border(left);
}
.borderTop {
  @include border(top);
}
.importButton {
  background-color: #1a73e8;
  color: white;
  cursor: pointer;
  padding-right: 14.5px;
  padding-left: 14.5px;
  font-weight: 400;
  font-size: 14px;
  height: 32px;
  border-color: #1a73e8;
  align-items: center;
  border-radius: 2px;
  display: inline-flex;
  flex: 0 0 auto;
  letter-spacing: 0.0892857143em;
  justify-content: center;
  outline: 0;
  text-decoration: none;
  text-indent: 0.0892857143em;
  transition-duration: 0.28s;
  transition-property: box-shadow, transform, opacity;
  position: relative;
  z-index: 999;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0px 3px 1px -2px rgb(0 0 0 / 20%), 0px 2px 2px 0px rgb(0 0 0 / 14%), 0px 1px 5px 0px rgb(0 0 0 / 12%);
}
.importButton:hover {
  filter: brightness(105%);
  box-shadow: 0px 2px 4px -1px rgb(0 0 0 / 20%), 0px 4px 5px 0px rgb(0 0 0 / 14%), 0px 1px 10px 0px rgb(0 0 0 / 12%);
}
</style>
